import { createRunAfterSignal } from 'serviceWorker/events/runAfterSignal';
import { swSignals } from 'serviceWorker/events/swSignals';
import { type ConfigUnavailableReason, getAuthCooldownRemainingMs } from 'serviceWorker/services/config';
import { logGroupedEntityFailure } from './entityFailureLog';
import { logConfigNotStoredOnce } from './logConfigNotStored';
import { LogLevel } from 'shared/types/logger';
import { getPullDiagnostics } from './pullDiagnostics';
import {
  createAuthRetryBudget,
  hasScheduledAuthCooldownRetry,
  scheduleAfterAuthCooldown,
} from './scheduleSwRetry';

/** How an operation that found no usable config goes on. */
export enum ConfigRetryPlan {
  /** An auth cooldown runs: it tries again when it ends. */
  afterCooldown = 'after-cooldown',
  /** No cooldown to wait for: it tries again once the page's config is applied. */
  onConfigApplied = 'on-config-applied',
  /**
   * A cooldown runs, but its retries are spent (the page keeps not giving a token): only the page's config
   * can bring it on, and it may never come.
   */
  onConfigAppliedAfterRetriesSpent = 'on-config-applied-after-retries-spent',
  /** Another call of the same operation already waits: one try serves all. */
  alreadyWaiting = 'already-waiting',
}

export interface ConfigRetry {
  /** The operation found no usable config: it goes on later, and never gives up while the worker lives. */
  wait(reason: ConfigUnavailableReason): ConfigRetryPlan;
  /** Whether a try after the cooldown or the applied config is already waiting. */
  isWaiting(): boolean;
  /** The operation had its config: the next absence starts with a fresh budget. */
  reset(): void;
}

/**
 * The way out of a missing config, shared by every download and sync: after
 * the auth cooldown when one runs (bounded by a budget of real retries),
 * otherwise once the page's config is applied (`swSignals.configApplied`).
 * Calls that come while one waits share it.
 */
export function createConfigRetry(retryKey: string, retry: () => void): ConfigRetry {
  const budget = createAuthRetryBudget();
  const afterConfigApplied = createRunAfterSignal(swSignals.configApplied, retry);

  const isWaiting = (): boolean => hasScheduledAuthCooldownRetry(retryKey) || afterConfigApplied.isWaiting();

  return {
    isWaiting,
    wait: (reason) => {
      void logConfigNotStoredOnce(reason);
      if (isWaiting()) {
        return ConfigRetryPlan.alreadyWaiting;
      }
      if (scheduleAfterAuthCooldown(retryKey, retry, budget)) {
        return ConfigRetryPlan.afterCooldown;
      }
      const areRetriesSpent = getAuthCooldownRemainingMs() > 0;
      afterConfigApplied.ask();
      return areRetriesSpent ? ConfigRetryPlan.onConfigAppliedAfterRetriesSpent : ConfigRetryPlan.onConfigApplied;
    },
    reset: () => {
      budget.attempts = 0;
    },
  };
}

/**
 * Logs that an entity waits for the config, once per wait: why, and how it goes
 * on. When only the page's config can bring it on after the cooldown retries
 * were spent, it is an error: the wait may never end, and readers give up
 * after a minute with nothing to say why.
 */
export function logWaitsForConfig(
  entityName: string,
  reason: ConfigUnavailableReason,
  plan: ConfigRetryPlan
): void {
  if (plan === ConfigRetryPlan.alreadyWaiting) {
    return;
  }
  const retriesSpent = plan === ConfigRetryPlan.onConfigAppliedAfterRetriesSpent;
  logGroupedEntityFailure(
    retriesSpent ? 'Pull waits for the config after the auth cooldown retries were spent' : 'Pull failed: fetch config unavailable',
    { entityName, setup: { ...getPullDiagnostics(), reason, retryPlan: plan } },
    { fingerprint: ['sw-pull-failed', 'fetch-config-unavailable', reason, ...(retriesSpent ? ['retries-spent'] : [])] },
    retriesSpent ? LogLevel.ERROR : LogLevel.INFO
  );
}
